import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Send, Car, MessageSquare, Clock, User, Bot } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { OilRequestForm } from '@/components/oil-request/OilRequestForm';

interface OilRequest {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  car_make: string;
  car_model: string;
  car_year: string;
  engine_type: string;
  status: 'pending' | 'responded' | 'completed';
  customer_message?: string;
  admin_response?: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
}

interface Message {
  id: string;
  oil_request_id: string;
  sender_type: 'customer' | 'admin';
  message: string;
  message_type: 'text' | 'image';
  image_url?: string;
  created_at: string;
}

const OilRequest = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [userRequests, setUserRequests] = useState<OilRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<OilRequest | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [lastReadMessages, setLastReadMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserRequests();
      // Load last read messages from localStorage
      const saved = localStorage.getItem('oil-request-last-read');
      if (saved) {
        setLastReadMessages(JSON.parse(saved));
      }
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (selectedRequest) {
      fetchMessages(selectedRequest.id);
      // Mark as read when selecting a request
      markRequestAsRead(selectedRequest.id);
    }
  }, [selectedRequest]);

  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const markRequestAsRead = (requestId: string) => {
    const now = new Date().toISOString();
    const updatedLastRead = { ...lastReadMessages, [requestId]: now };
    setLastReadMessages(updatedLastRead);
    localStorage.setItem('oil-request-last-read', JSON.stringify(updatedLastRead));
  };

  const hasUnreadMessages = (request: OilRequest) => {
    const lastRead = lastReadMessages[request.id];
    if (!lastRead) return request.status === 'responded';
    
    // Check if the request was updated after last read
    return request.updated_at && new Date(request.updated_at) > new Date(lastRead);
  };

  const fetchUserRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('oil_requests')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as 'pending' | 'responded' | 'completed'
      }));

      setUserRequests(typedData);
      
      // Auto-select the first request if available
      if (typedData.length > 0 && !selectedRequest) {
        setSelectedRequest(typedData[0]);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('فشل في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('oil_request_messages')
        .select('*')
        .eq('oil_request_id', requestId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const typedMessages = (data || []).map(message => ({
        ...message,
        sender_type: message.sender_type as 'customer' | 'admin',
        message_type: (message.message_type as 'text' | 'image') || 'text'
      }));

      setMessages(typedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('فشل في تحميل الرسائل');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedRequest || !newMessage.trim()) return;

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('oil_request_messages')
        .insert({
          oil_request_id: selectedRequest.id,
          sender_type: 'customer',
          message: newMessage.trim(),
          message_type: 'text'
        });

      if (error) throw error;

      // Update the request status to show there's activity
      await supabase
        .from('oil_requests')
        .update({ 
          status: 'responded',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      setNewMessage('');
      fetchMessages(selectedRequest.id);
      fetchUserRequests();
      toast.success('تم إرسال الرسالة بنجاح');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('فشل في إرسال الرسالة');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewRequest = async (requestData: any) => {
    try {
      const { data, error } = await supabase
        .from('oil_requests')
        .insert({
          ...requestData,
          user_id: user?.id
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('تم إرسال طلبك بنجاح');
      setShowNewRequestForm(false);
      fetchUserRequests();
      
      // Auto-select the new request
      if (data) {
        const typedData = {
          ...data,
          status: data.status as 'pending' | 'responded' | 'completed'
        };
        setSelectedRequest(typedData);
      }
    } catch (error) {
      console.error('Error creating request:', error);
      toast.error('فشل في إرسال الطلب');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">في الانتظار</Badge>;
      case 'responded':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">نشط</Badge>;
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">مكتمل</Badge>;
      default:
        return <Badge variant="secondary">غير معروف</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ar-SA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-autocare-blue mx-auto mb-4"></div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (showNewRequestForm) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-grow pt-20 pb-12">
          <div className="page-container max-w-4xl">
            <div className="mb-8">
              <Button 
                variant="outline" 
                onClick={() => setShowNewRequestForm(false)}
                className="mb-4"
              >
                العودة إلى المحادثات
              </Button>
              <h1 className="text-3xl font-bold text-autocare-blue mb-2">طلب زيت سيارة جديد</h1>
              <p className="text-gray-600">املأ البيانات لإنشاء طلب جديد</p>
            </div>
            <OilRequestForm onSubmit={handleNewRequest} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="flex-grow pt-20 pb-12">
        <div className="page-container max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-autocare-blue mb-2">طلبات الزيوت</h1>
            <p className="text-gray-600">تابع طلباتك وتحدث مع فريق الدعم</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Requests Sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      طلباتي ({userRequests.length})
                    </CardTitle>
                    <Button 
                      onClick={() => setShowNewRequestForm(true)}
                      size="sm"
                    >
                      طلب جديد
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {userRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">لا توجد طلبات</p>
                      <Button onClick={() => setShowNewRequestForm(true)}>
                        إنشاء طلب جديد
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {userRequests.map((request) => (
                        <div
                          key={request.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors relative ${
                            selectedRequest?.id === request.id
                              ? 'border-autocare-blue bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedRequest(request)}
                        >
                          {hasUnreadMessages(request) && (
                            <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                          )}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2 text-sm">
                              <Car className="h-4 w-4" />
                              <span>{request.car_make} {request.car_model}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {hasUnreadMessages(request) && (
                                <Badge variant="destructive" className="text-xs px-1 py-0 h-5">
                                  جديد
                                </Badge>
                              )}
                              {getStatusBadge(request.status)}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatDate(request.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2">
              {selectedRequest ? (
                <Card className="h-[600px] flex flex-col">
                  <CardHeader className="border-b">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Car className="h-5 w-5" />
                          {selectedRequest.car_make} {selectedRequest.car_model} ({selectedRequest.car_year})
                        </CardTitle>
                        <CardDescription>
                          نوع المحرك: {selectedRequest.engine_type}
                        </CardDescription>
                      </div>
                      {getStatusBadge(selectedRequest.status)}
                    </div>
                  </CardHeader>

                  {/* Messages Area */}
                  <CardContent className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-4">
                      {/* Initial request message */}
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-sm mb-2">
                              <strong>طلب جديد للسيارة:</strong>
                            </p>
                            <p className="text-sm">
                              {selectedRequest.car_make} {selectedRequest.car_model} ({selectedRequest.car_year})
                              <br />
                              نوع المحرك: {selectedRequest.engine_type}
                            </p>
                            {selectedRequest.customer_message && (
                              <p className="text-sm mt-2 border-t pt-2">
                                <strong>رسالة إضافية:</strong> {selectedRequest.customer_message}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 mt-1 block">
                            {formatTime(selectedRequest.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Chat messages */}
                      {messages.map((message) => (
                        <div key={message.id} className={`flex gap-3 ${message.sender_type === 'customer' ? '' : 'flex-row-reverse'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            message.sender_type === 'customer' 
                              ? 'bg-blue-100' 
                              : 'bg-green-100'
                          }`}>
                            {message.sender_type === 'customer' ? (
                              <User className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Bot className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className={`rounded-lg p-3 ${
                              message.sender_type === 'customer' 
                                ? 'bg-blue-50' 
                                : 'bg-green-50'
                            }`}>
                              {message.message_type === 'image' && message.image_url ? (
                                <div className="mb-2">
                                  <img 
                                    src={message.image_url} 
                                    alt="صورة مرسلة" 
                                    className="max-w-xs rounded-lg cursor-pointer"
                                    onClick={() => window.open(message.image_url, '_blank')}
                                  />
                                  {message.message && message.message !== 'صورة' && (
                                    <p className="text-sm mt-2">{message.message}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm">{message.message}</p>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">
                              {formatTime(message.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>

                  {/* Message Input */}
                  <div className="border-t p-4">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="اكتب رسالتك..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="resize-none"
                        rows={2}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={submitting || !newMessage.trim()}
                        className="self-end"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center h-64">
                    <div className="text-center text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>حدد طلباً من القائمة لبدء المحادثة</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default OilRequest;
